'use client';
import { useState } from 'react';
import { useT } from '../../lib/i18n';
import type { ProductListItem } from '../../lib/site';
import { Icon } from '../Icon';
import { toast } from '../Toast';
import { blockedLabel, buyable, discountShort, priceLines, statusLabel, won, type PriceLine } from './price';
import { platformLabel } from './labels';
import { SmartLink } from '../SmartLink';
import { cartChanged } from '../cartCount';

/**
 * 가격 — 가이드는 이걸 **두 조각**으로 나눠 둔다(`lecture-list.html` 카드 4).
 *
 *  ① `course-price-option` — 단가를 고르는 목록(공용·PC·모바일). `course-body` 안,
 *     `course-footer` **앞**에 온다. 단가 세분화를 쓰는 마켓에만 있다.
 *  ② `course-footer` — 고른 값 + 담기·신청 버튼.
 *
 * 처음엔 이 구조를 못 찾고 `price-platform` 을 새로 만들었다 — 가이드에 이미 있으므로 되돌렸다
 * (공용셋 §4 — 클래스를 지어내지 않는다. 없어 보이면 먼저 가이드 CSS 를 찾아본다).
 */

/** 고른 단가 — 고르는 목록과 푸터가 같은 상태를 봐야 하므로 부모가 들고 있는다 */
export function usePrice(p: ProductListItem, priceDetail: boolean) {
  const lines = priceLines(p, priceDetail);
  const [picked, setPicked] = useState(0);
  return { lines, picked, setPicked, line: lines[picked] ?? lines[0] };
}

/**
 * ① 단가 고르기 — 한 줄뿐이면 고를 것이 없으니 그리지 않는다.
 *
 * **간단형은 고르지 않고 보여만 준다**(`readOnly`) — 가이드의 '카드형 심플'·'리스트형 심플'
 * 은 라디오 없이 값만 나열하고, CSS 도 `cursor: default` 로 그렇게 잡혀 있다.
 */
export function PriceOptions({ p, lines, picked, onPick, readOnly, className, showSingle, withDiscount }: {
  p: ProductListItem;
  lines: PriceLine[];
  picked: number;
  onPick: (i: number) => void;
  readOnly?: boolean;
  className?: string;
  /**
   * 한 줄뿐이어도 그린다 — **단가 세분화를 쓰는 마켓**이 넘긴다(2026-08-24 사용자 지적
   * "단가 1개만 설정한 상품은 전체·PC·모바일 중 무엇인지 나타나지 않음").
   * 그 마켓에서는 값 하나만 보여 주면 어느 단가인지 알 수 없다. 고를 것은 없으니 라디오는 없다.
   */
  showSingle?: boolean;
  /** 단가마다 할인액을 적을지 — **리스트형만**(가이드 카드형 항목에는 없다) */
  withDiscount?: boolean;
}) {
  const t = useT();
  if (lines.length <= 1 && !showSingle) return null;
  /*
   * 한 줄뿐이면 **고를 것이 없다** — 라디오는 안 그린다.
   * 고를 수 있는 자리에서 한 줄뿐이면 그 줄은 이미 **고른 줄**이므로 선택 표시(`active`)를
   * 준다(2026-08-24 사용자 지적 — 하나뿐인데 안 고른 것처럼 보였다).
   *
   * **읽기 전용(간단형)에서는 강조하지 않는다** — 줄이 하나여도 마찬가지다
   * (2026-08-26 사용자 제보 "심플에서 가격에 배경이 들어가 있다"). 가이드 심플은 값만 적고
   * 강조가 없다. 고를 수 없는 자리에서 한 줄을 칠해 두면 **고른 것처럼** 읽힌다.
   */
  const single = lines.length <= 1;
  const ro = readOnly || single;
  const markActive = !readOnly;
  return (
    <div className={`course-price-option${className ? ` ${className}` : ''}`}>
      <div className="course-price-list">
        {lines.map((l, i) => (
          <label key={l.platform} className={`course-price-item${markActive && i === picked ? ' active' : ''}`}>
            {!ro && (
              <input type="radio" name={`prd-${p.id}`} checked={i === picked} onChange={() => onPick(i)} />
            )}
            <span className="price-label">{platformLabel(l.platform, t)}</span>
            {/*
              단가별 할인 — **리스트형에서만**(2026-08-25 가이드 개정: 카드형 항목엔 없다).
              값은 그 단가의 원가와 판매가 차이다 — 상품 대표 할인이 아니라 고른 단가의 것이다.
            */}
            {withDiscount && l.original > l.price && (
              <span className="course-discount">-{won(l.original - l.price)}{t('prd.money')}</span>
            )}
            <span className="price-val">{won(l.price)}{t('prd.money')}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * ② 값 + 버튼.
 *
 * 살 수 없는 상태(판매예정·판매종료·신청기간 밖)면 값 자리에 그 문구를 그린다 —
 * 가이드가 `course-price soon` 으로 그 자리를 쓴다.
 *
 * **간단형에는 버튼이 없다** — 가이드 "카드형 심플"·"리스트형 심플" 은 값 하나뿐이다.
 */
export function PriceFooter({ p, line, href, actions = true, block, showDiscount = true, priceOnly, lines, buyLabel, showSingle, billing }: {
  p: ProductListItem;
  line: PriceLine;
  /**
   * 상세 경로 — **카드가 쓰는 것과 같은 값을 받는다.**
   * 전에는 `/lectures/{id}` 로 박아 둬서 교재 목록의 버튼이 강좌 경로로 갔다(2026-08-22).
   */
  href: string;
  /** 담기·신청 버튼 — 간단형은 false */
  actions?: boolean;
  /** 리스트는 값과 버튼을 위아래로 놓는다(가이드 `clist-actions`) */
  block?: boolean;
  /**
   * 할인 표기를 금액 옆에 둘지 — 기본은 켠다.
   * 전에는 296px 카드에 자리가 없어 카드형에서 끄고 썸네일 배지로 올렸는데,
   * 2026-08-25 가이드 개정에서 `course-price-wrap`(flex·wrap)이 그 자리를 만들어
   * **가이드도 푸터에 적는다**. 간단형(`priceOnly`)만 여전히 값 하나로 둔다.
   */
  showDiscount?: boolean;
  /**
   * **판매가만** 그린다 — 원가(취소선)도 할인 표기도 없다.
   * 간단형(카드·리스트 심플)이 쓴다(2026-08-22 사용자 확정). 가이드의 간단형은 값 하나뿐이라
   * 할인까지 얹으면 '간단'이 아니게 된다.
   */
  priceOnly?: boolean;
  /**
   * 단가 목록 — **간단형에서 단가 세분화를 쓰는 마켓**이 넘긴다.
   * 가이드는 이때 값 하나가 아니라 종류별 값을 나열한다(카드4·리스트4).
   * 여기서 갈라 두면 카드·리스트가 같은 규칙을 쓴다.
   */
  lines?: PriceLine[];
  /** 단가 세분화 마켓 — 한 줄뿐이어도 어느 단가인지 이름표를 보여 준다 */
  showSingle?: boolean;
  /**
   * 주 버튼 글자 — 비우면 `수강신청`.
   * **교재는 `구매하기`** 다(가이드 `book-list.html`). 수강하는 물건이 아니라 사는 물건이다.
   */
  buyLabel?: string;
  /**
   * 구독 보기 — **판매 얼굴을 벗는다**(2026-09-09 사용자 확정, 현행 `/goods/lecture/billing`).
   * 구독료로 이미 열린 강좌라 값도 [담기]도 뜻이 없다. 자리에는 [수강하기] 하나만 둔다.
   *
   * 판매 상태(예정·마감·품절)도 보지 않는다 — 판매가 끝난 강좌도 구독 범위면 열린다.
   * 간단형(`actions={false}`)에서도 버튼은 그린다: 값을 지운 자리에 버튼마저 없으면
   * 눌러 들어갈 곳이 사라진다.
   */
  billing?: boolean;
}) {
  const t = useT();
  const status = statusLabel(p, t);
  if (billing) {
    /*
     * 카드형은 버튼이 **가로를 다 채운다**(2026-09-09 사용자 지시). 값을 지운 자리에 작은
     * 버튼 하나만 남으면 카드 아래가 비어 보인다. 리스트형(`block`)은 줄 오른쪽 칸이 좁아
     * 원래 크기 그대로다 — 거기서 늘리면 강좌명 칸을 먹는다.
     */
    return (
      <div className={`d-flex gap5${block ? '' : ' billing-cta'}`}>
        <SmartLink className="action-btn action-submit btn-sm" href={href}>{t('prd.studyNow')}</SmartLink>
      </div>
    );
  }
  /** 살 수는 없지만 **가격은 보여 줄** 사유(품절) — 버튼만 바뀐다 */
  const blocked = blockedLabel(p, t);
  /*
   * 할인 표기 — 가이드 형식 그대로 `10%` / `-5,000원`(2026-08-25 개정).
   * `discountLabel` 은 정률 앞에 `↓` 를 붙이던 현행 표기라 여기서는 쓰지 않는다.
   */
  const dc = !priceOnly && showDiscount ? discountShort(p, t('prd.money')) : null;
  const free = line.price <= 0;
  const blockCls = block ? ' mb8 d-block' : '';

  if (status) {
    return (
      <>
        <span className={`course-price ${status.className}${blockCls}`}>{status.text}</span>
        {actions && (
          <div className="d-flex gap5">
            <button className="action-btn action-cancel btn-sm" disabled>{status.text}</button>
          </div>
        )}
      </>
    );
  }

  // 간단형 + 단가 세분화 — 값 하나가 아니라 종류별 값을 나열한다(가이드)
  if (priceOnly && lines && (lines.length > 1 || showSingle)) {
    return <PriceOptions p={p} lines={lines} picked={0} onPick={() => {}} readOnly showSingle={showSingle}
      className={block ? 'mb8' : undefined} />;
  }

  return (
    <>
      {/*
        진행 중 혜택 — **최종가격 바로 위에 늘 떠 있는 말풍선**(2026-08-24 사용자 지시).
        호버로 여는 툴팁이 아니다(모바일에는 호버가 없다). 값 위에 절대배치로 얹어
        푸터의 한 줄 배치를 건드리지 않는다. 자세한 조건은 상세가 말한다.
      */}
      <div className={`course-price-wrap${blockCls}`}>
        {/* 혜택 표시 — 가이드가 `event-flag` 로 정식화했다(2026-08-25 디자인 반영) */}
        {p.hasBenefit && <span className="event-flag">{t('prd.benefit.tip')}</span>}
        {/* 할인 표기도 가이드 클래스(`course-discount`) — 괄호를 두르지 않는다 */}
        {dc && <span className="course-discount">{dc}</span>}
        <span className={`course-price${free ? ' free' : ''}`}>
        {/*
          **원가(취소선)는 그리지 않는다**(2026-08-25 가이드 개정) —
          가이드는 `할인율/할인금액 + 최종가` 만 적는다. 원가는 상세가 말한다.
        */}
        {free ? t('prd.free') : `${won(line.price)}${t('prd.money')}`}
        </span>
      </div>
      {actions && (
        <div className="d-flex gap5">
          {/*
            담기 · 바로 결제 — `수강신청`(교재는 `구매하기`)은 **결제 화면으로 바로 간다**
            (2026-08-25 사용자 지시). 장바구니를 거치지 않는다 — 현행도 결제 화면이 상품
            정보를 바로 받았고 장바구니 id 는 장바구니에서 왔을 때만 붙었다.
            고른 단가(`line.platform`)를 같이 넘긴다 — 세분화 마켓에서는 그것이 곧 값이다.
            살 수 없는 상품이면 상세로 보낸다(눌러도 허탕인 버튼을 두지 않는다).
          */}
          {/*
            품절 — **가격은 그대로 두고 버튼만 잠근다**(2026-09-02 사용자 지시). 전에는 재고를
            결제 단계에서야 봐서 [바로구매] 를 누르고 결제까지 간 뒤에 "재고가 부족합니다" 를 만났다.
          */}
          {blocked ? (
            <button type="button" className="action-btn action-cancel btn-sm" disabled>{blocked}</button>
          ) : (
            <>
              <AddCartButton productId={p.id} platform={line.platform} />
              <SmartLink className="action-btn action-submit btn-sm"
                href={buyable(p) ? `/checkout?buy=${p.id}:1:${line.platform}` : href}>
                {buyable(p) ? (buyLabel ?? t('prd.enroll')) : t('prd.viewDetail')}
              </SmartLink>
            </>
          )}
        </div>
      )}
    </>
  );
}

/**
 * 장바구니 담기 — 고른 단가와 함께 넣는다(세분화를 쓰는 마켓은 종류가 값이다).
 * 비로그인은 로그인으로 보낸다. 목록 어디서든 같은 동작이어야 하므로 여기 한 곳에 둔다.
 */
function AddCartButton({ productId, platform }: { productId: number; platform: string }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  /*
   * 낙관적 반응(UX 채택안 #4·#11) — 누르는 즉시 **버튼이** 체크로 바뀐다. alert 는 화면을 막아 폐지했다.
   *
   * **토스트는 낙관적으로 띄우지 않는다**(2026-09-02 사용자 지적) — 전에는 누르자마자
   * "장바구니에 담았습니다" 를 띄우고, 서버가 거절하면(이미 담김·재고 부족) 오류 토스트가
   * 뒤따라 **두 개가 겹쳤다**. 담겼는지는 서버만 아는 일이라 답을 듣고 말한다.
   * 버튼 모양만 먼저 바뀌므로 누른 느낌은 그대로다.
   */
  const [added, setAdded] = useState(false);

  const add = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
    try {
      const res = await fetch('/api/members/me/cart', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ productId, platform }),
      });
      if (res.status === 401) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        setAdded(false);
        toast(body.message ?? t('prd.cartFailed'), 'error');
        return;
      }
      toast(t('prd.addedToCart'));
      cartChanged(); // 헤더의 장바구니 점을 다시 세게 한다
    } catch {
      setAdded(false);
      toast(t('acc.err.network'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" className={`action-btn action-cancel btn-sm course-cart${added ? ' is-added' : ''}`}
      disabled={busy} onClick={(e) => void add(e)} aria-label={t('prd.addCart')}>
      <Icon name={added ? 'check' : 'shopping-cart'} />
    </button>
  );
}
